import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, finalize, Observable, of, tap } from 'rxjs';

import { ApiProtectedService } from '@app/services/api/api.protected.service';
import { PortalReferralProgramNode } from '@app/services/api/api.types';

export type ReferralProgramLoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: PortalReferralProgramNode }
  | { status: 'error' };

@Injectable({
  providedIn: 'root',
})
export class ReferralProgramService {
  private loadState$ = new BehaviorSubject<ReferralProgramLoadState>({ status: 'idle' });

  private loading = false;

  constructor(private apiProtectedService: ApiProtectedService) {}

  getState(): Observable<ReferralProgramLoadState> {
    return this.loadState$.asObservable();
  }

  getStateValue(): ReferralProgramLoadState {
    return this.loadState$.value;
  }

  getProgram(): PortalReferralProgramNode | null {
    const state = this.loadState$.value;
    return state.status === 'ready' ? state.data : null;
  }

  load(force = false) {
    if (this.loading) {
      return;
    }

    const current = this.loadState$.value;
    if (!force && current.status === 'ready') {
      return;
    }

    this.loading = true;
    this.loadState$.next({ status: 'loading' });

    this.apiProtectedService
      .portalReferralProgram()
      .pipe(
        tap((data) => {
          if (data) {
            this.loadState$.next({ status: 'ready', data });
          } else {
            this.loadState$.next({ status: 'error' });
          }
        }),
        catchError(() => {
          this.loadState$.next({ status: 'error' });
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe();
  }

  reload() {
    this.load(true);
  }

  reset() {
    this.loadState$.next({ status: 'idle' });
  }
}
